import { isSearchedTriggerBlock } from '../../LocalMeshBot/isSearchedTriggerBlock';
import { GROUP } from 'constants/MeshbotConstant';

/**
 * Resolves the current group ID while traversing the trigger tree.
 * If the block is of type 'group', it returns its own ID as the new group context.
 * Otherwise, the previous `currentGroupId` is preserved.
 *
 * @param {Object} block - The current block being traversed.
 * @param {string | undefined} currentGroupId - The ID of the group currently in context.
 * @returns {string | undefined} The updated group ID context.
 */
const resolveCurrentGroupId = (block, currentGroupId) => (block?.type === GROUP ? block.id : currentGroupId);

/**
 * Recursively searches for a trigger block inside a nested block tree by ID and optional group context.
 *
 * The search is depth-first and respects group structure — if a `groupId` is provided,
 * only blocks within that group are eligible to match. The correct `groupId` context is determined
 * during traversal using `resolveCurrentGroupId`.
 *
 * @param {Array<Object>} blocks - Array of blocks to search in.
 * @param {string} id - The ID of the trigger to find.
 * @param {string | undefined} groupId - Optional group ID that narrows down the search scope.
 * @param {string | undefined} currentGroupId - The current group ID in context during recursion.
 * @returns {Object | undefined} The matched trigger block, or undefined if not found.
 *
 * @example
 * const block = findNestedBlockById(rule.when, 'trigger-123', 'group-1');
 */
export function findNestedBlockById(blocks, id, groupId, currentGroupId) {
    if (!Array.isArray(blocks)) {
        return;
    }

    for (const block of blocks) {
        if (isSearchedTriggerBlock(groupId, id, currentGroupId, block?.id)) {
            return block;
        }

        const nested = findNestedBlockById(block?.blocks, id, groupId, resolveCurrentGroupId(block, currentGroupId));

        if (nested) {
            return nested;
        }
    }
}
